import { useEffect, useState } from "react";
import { Copy, KeyRound, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE, apiError } from "../../lib/api";

const card = "rounded-2xl border border-adminborder bg-admincard";
const darkInput = "w-full rounded-lg border border-adminborder bg-adminbg px-3.5 py-2.5 text-sm text-slate-200 outline-none focus:border-saffron";
const codeBlock = "overflow-x-auto rounded-xl border border-adminborder bg-adminbg p-4 font-mono text-xs leading-relaxed text-slate-300";

const ENDPOINT = `${API_BASE}/api/external/v1/international-rates`;

const REQUEST_EXAMPLE = `{
  "destination_country": "United States",
  "weight": 2.5,
  "service_type": "Express",        // optional
  "zipcode": "90001"                // optional — exact zone for AU / NZ / Canada
}`;

const RESPONSE_EXAMPLE = `{
  "destination_country": "UAE",
  "chargeable_weight": 8.0,
  "currency": "INR",
  "price_on_request": false,
  "zone": null,
  "zones": [],
  "zone_approx": false,
  "rates": [
    {
      "courier_name": "IndiaFast SELF",
      "service": "self",
      "transit_days": "",
      "zone": "",
      "price": 3520.0,
      "currency": "INR"
    },
    {
      "courier_name": "IndiaFast SELF Duty Paid",
      "service": "dutypaid",
      "transit_days": "",
      "zone": "",
      "price": 5072.0,
      "currency": "INR"
    },
    {
      "courier_name": "UPS",
      "service": "express",
      "transit_days": "5-6 Days",
      "zone": "1",
      "price": 7557.0,
      "currency": "INR"
    },
    {
      "courier_name": "FedEx",
      "service": "express",
      "transit_days": "5-6 Days",
      "zone": "A",
      "price": 8992.0,
      "currency": "INR"
    }
  ]
}`;

const CURL_EXAMPLE = `curl -X POST "${ENDPOINT}" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ifk_YOUR_KEY_HERE" \\
  -d '{"destination_country": "United States", "weight": 2.5}'`;

export default function AdminApiAccess() {
  const [creds, setCreds] = useState([]);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [revoking, setRevoking] = useState("");

  const load = () => api.get("/admin/api-credentials").then((r) => setCreds(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) { toast.error("Enter a name for the credential"); return; }
    setCreating(true);
    try {
      const r = await api.post("/admin/api-credentials", { name: name.trim() });
      setNewKey(r.data.api_key);
      setName("");
      toast.success("API key created — copy it now, it will not be shown again");
      load();
    } catch (e) { toast.error(apiError(e)); }
    setCreating(false);
  };

  const revoke = async (id) => {
    setRevoking(id);
    try {
      await api.post(`/admin/api-credentials/${id}/revoke`);
      toast.success("API key revoked");
      load();
    } catch (e) { toast.error(apiError(e)); }
    setRevoking("");
  };

  const copy = async (text, msg) => {
    try { await navigator.clipboard.writeText(text); toast.success(msg); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };

  return (
    <div data-testid="admin-api-access" className="space-y-8">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">API Access</h1>
        <p className="mt-1 text-sm text-slate-500">Issue API keys to partners so they can pull live international rates from the IndiaFast price engine.</p>
      </div>

      <div className={`${card} p-5 sm:p-6`} data-testid="api-credentials-card">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-saffron" />
          <h2 className="text-lg font-bold text-white">API Credentials</h2>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="api-credential-name-input"
                 placeholder="Partner name, e.g. Acme Exports" className={`${darkInput} max-w-xs`}
                 onKeyDown={(e) => e.key === "Enter" && create()} />
          <button onClick={create} disabled={creating} data-testid="api-credential-create-btn"
                  className="flex items-center gap-2 rounded-lg bg-saffron px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-saffron-hover disabled:opacity-50">
            <Plus className="h-4 w-4" /> {creating ? "Creating…" : "Generate API Key"}
          </button>
        </div>

        {newKey && (
          <div className="mt-4 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4" data-testid="api-key-created-panel">
            <p className="text-sm font-bold text-emerald-400">Key created — copy it now. It will never be shown again.</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <code className="break-all rounded-lg bg-adminbg px-3 py-2 font-mono text-xs text-emerald-300" data-testid="api-key-created-value">{newKey}</code>
              <button onClick={() => copy(newKey, "API key copied")} data-testid="api-key-copy-btn"
                      className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-600">
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
              <button onClick={() => setNewKey(null)} data-testid="api-key-dismiss-btn"
                      className="rounded-lg border border-adminborder px-3 py-2 text-xs font-bold text-slate-400 hover:text-white">Hide</button>
            </div>
          </div>
        )}

        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="api-credentials-table">
            <thead>
              <tr className="border-b border-adminborder text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">Key</th><th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Created</th><th className="px-4 py-3">Last Used</th><th className="px-4 py-3">Calls</th><th className="px-4 py-3">Top Countries</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {creds.map((c) => (
                <tr key={c.id} className="border-b border-adminborder/60 last:border-0 hover:bg-white/5" data-testid={`api-credential-row-${c.id}`}>
                  <td className="px-4 py-3 font-bold text-white">{c.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">{c.key_prefix}…</td>
                  <td className="px-4 py-3">
                    <span data-testid={`api-credential-status-${c.id}`}
                          className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${c.status === "active" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                      {c.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">{(c.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{c.last_used_at ? c.last_used_at.slice(0, 16).replace("T", " ") : "Never"}</td>
                  <td className="px-4 py-3 text-xs font-bold text-white" data-testid={`api-credential-calls-${c.id}`}>{c.total_calls ?? 0}</td>
                  <td className="px-4 py-3 text-xs text-slate-400" data-testid={`api-credential-countries-${c.id}`}>
                    {(c.top_countries || []).length
                      ? c.top_countries.map((t) => `${t.country} (${t.calls})`).join(", ")
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {c.status === "active" && (
                      <button onClick={() => revoke(c.id)} disabled={revoking === c.id} data-testid={`api-credential-revoke-${c.id}`}
                              className="flex items-center gap-1 rounded-lg border border-red-500/40 px-3 py-1.5 text-xs font-bold text-red-400 transition-colors hover:bg-red-500/10 disabled:opacity-50">
                        <Trash2 className="h-3 w-3" /> Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!creds.length && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500" data-testid="api-credentials-empty">No API credentials yet — generate the first one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className={`${card} p-5 sm:p-6`} data-testid="api-docs-card">
        <h2 className="text-lg font-bold text-white">API Documentation — International Price API</h2>
        <p className="mt-1 text-sm text-slate-500">Real-time international courier rates from the IndiaFast price engine. Prices are customer selling prices in INR, identical to the public rate calculator.</p>

        <div className="mt-5 space-y-6">
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-saffron">Endpoint</h3>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <code className={`${codeBlock} min-w-0 flex-1`} data-testid="api-docs-endpoint">POST {ENDPOINT}</code>
              <button onClick={() => copy(ENDPOINT, "Endpoint copied")} data-testid="api-docs-copy-endpoint"
                      className="flex items-center gap-1.5 rounded-lg border border-adminborder px-3 py-2 text-xs font-bold text-slate-300 hover:border-saffron hover:text-saffron">
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-saffron">Authentication</h3>
            <p className="mt-2 text-sm text-slate-400">Send your API key on every request via the <code className="rounded bg-adminbg px-1.5 py-0.5 font-mono text-xs text-slate-200">X-API-Key</code> header. Keys are shown only once at creation — store them securely. Revoked or missing keys return <code className="rounded bg-adminbg px-1.5 py-0.5 font-mono text-xs text-slate-200">401</code>.</p>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-saffron">Request body</h3>
              <pre className={`${codeBlock} mt-2`} data-testid="api-docs-request-example">{REQUEST_EXAMPLE}</pre>
              <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-slate-500">
                <li><span className="font-mono text-slate-300">destination_country</span> — required, e.g. "United States", "UAE", "UK"</li>
                <li><span className="font-mono text-slate-300">weight</span> — required kg weight (or send <span className="font-mono text-slate-300">boxes</span> with weight/length/width/height per box)</li>
                <li><span className="font-mono text-slate-300">service_type</span> — optional, filter by service (e.g. Express)</li>
                <li><span className="font-mono text-slate-300">zipcode</span> — optional delivery postcode; gives exact zone pricing for Australia, New Zealand & Canada</li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-saffron">Response</h3>
              <pre className={`${codeBlock} mt-2`} data-testid="api-docs-response-example">{RESPONSE_EXAMPLE}</pre>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-saffron">cURL example</h3>
            <pre className={`${codeBlock} mt-2`} data-testid="api-docs-curl-example">{CURL_EXAMPLE}</pre>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-saffron">Error codes</h3>
              <div className="mt-2 overflow-hidden rounded-xl border border-adminborder" data-testid="api-docs-errors">
                {[
                  ["400", "Missing destination_country or weight"],
                  ["401", "Missing, invalid or revoked API key"],
                  ["422", "Invalid field types in request body"],
                ].map(([code, desc]) => (
                  <div key={code} className="flex items-center gap-3 border-b border-adminborder/60 px-4 py-2.5 last:border-0">
                    <code className="rounded bg-red-500/15 px-2 py-0.5 font-mono text-xs font-bold text-red-400">{code}</code>
                    <span className="text-xs text-slate-400">{desc}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-saffron">Notes</h3>
              <ul className="mt-2 list-inside list-disc space-y-1.5 text-xs text-slate-400" data-testid="api-docs-notes">
                <li>When no rate card covers the requested weight/country, the API returns <span className="font-mono text-slate-200">price_on_request: true</span> with an empty rates list — prices are never extrapolated beyond defined slabs.</li>
                <li>Chargeable weight is billed per whole kg (volumetric applies when box dimensions are sent).</li>
                <li>For zoned countries (AU/NZ/CA) without a postcode, the lowest zone price is returned with a <span className="font-mono text-slate-200">zone_note</span>.</li>
                <li>All prices are final customer prices in INR.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
